import { announce } from "@kovernow/flows/announce";
import { createFormatter, type Formatter } from "@kovernow/flows/format";
import { homeQueryKeys, resolveProductTiles } from "@kovernow/flows/home";
import { toRecoveryAction } from "@kovernow/flows/recovery";
import {
  categoryLabelKey,
  categoryOptions,
  creditChipFor,
  DEFAULT_VAULT_PARAMS,
  displayValue,
  expiredBannerRoute,
  filterItems,
  hasActiveFilters,
  isNotCovered,
  itemName,
  itemPhoto,
  itemRoute,
  parseVaultParams,
  SEARCH_DEBOUNCE_MS,
  toDisplayStatus,
  toVaultParams,
  uncoveredSet,
  valueAtRisk,
  vaultQueryKeys,
  vaultTotal,
  type VaultItem,
  type VaultParams,
} from "@kovernow/flows/vault";
import { Button } from "@kovernow/react-ui/components/button";
import { CreditChip } from "@kovernow/react-ui/components/credit-chip";
import { EmptyState } from "@kovernow/react-ui/components/empty-state";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Input } from "@kovernow/react-ui/components/input";
import { ItemCard } from "@kovernow/react-ui/components/item-card";
import { Label } from "@kovernow/react-ui/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@kovernow/react-ui/components/select";
import { StatusBadge } from "@kovernow/react-ui/components/status-badge";
import { Switch } from "@kovernow/react-ui/components/switch";
import { ValueAtRisk } from "@kovernow/react-ui/components/value-at-risk";
import { VaultSummary } from "@kovernow/react-ui/components/vault-summary";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { categoryIcon } from "../components/category-icons";
import { Page } from "../components/Page";
import { Recovery } from "../components/Recovery";
import { useServices } from "../core/app-context";
import { useT } from "../core/use-t";

const STATUS_OPTIONS: Array<{ value: VaultParams["status"]; labelKey: string }> = [
  { value: "all", labelKey: "vault.filters.all" },
  { value: "uninsured", labelKey: "vault.filters.notCovered" },
  { value: "active", labelKey: "vault.filters.active" },
  { value: "pending", labelKey: "vault.filters.pending" },
  { value: "failed", labelKey: "vault.filters.failed" },
];

type FocusTarget = { kind: "item"; id: string } | { kind: "heading" } | null;

/**
 * W-3 Vault: one `GET /items/list`, filtered and searched on the client
 * (flows `vault`), filters in the URL (`?q=&status=&category=&hidden=1`,
 * written with replace). VaultSummary reuses `dashboardData.vault`, so the
 * counts match Home; "Not covered" follows the server's `uncoveredItemIds`.
 * Hide/unhide is server-confirmed (no optimistic move).
 */
export function Vault() {
  const t = useT();
  const { vaultApi, homeApi, config, format } = useServices();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseVaultParams(searchParams), [searchParams]);

  // Stable focus key: filter changes (replace) never move focus to the h1;
  // the initial load (key "default") never steals focus (null).
  const [focusKey] = useState<string | null>(() => (location.key === "default" ? null : location.pathname));

  const items = useQuery({ queryKey: vaultQueryKeys.items(), queryFn: () => vaultApi.list() });
  const dashboard = useQuery({ queryKey: homeQueryKeys.dashboard(), queryFn: () => homeApi.dashboard() });
  const flags = useQuery({ queryKey: homeQueryKeys.flags(), queryFn: () => homeApi.flags(), retry: false });
  const credit = useQuery({ queryKey: vaultQueryKeys.credit(), queryFn: () => vaultApi.credit(), retry: false });

  // Hiding the last item swaps the list (and its heading) for the first-time
  // state; focus that state's heading instead of letting it drop to <body>.
  const focusFirstTime = useRef(false);
  const firstTimeHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focusFirstTime.current && firstTimeHeadingRef.current) {
      focusFirstTime.current = false;
      firstTimeHeadingRef.current.focus();
    }
  });

  const writeParams = useCallback(
    (patch: Partial<VaultParams>) => {
      setSearchParams((prev) => toVaultParams({ ...parseVaultParams(prev), ...patch }), { replace: true });
    },
    [setSearchParams],
  );

  // Search input: local state, written to the URL after 250 ms.
  const [qInput, setQInput] = useState(params.q);
  const lastWrittenQ = useRef(params.q);
  useEffect(() => {
    // An outside change (Clear filters, back/forward) updates the input.
    if (params.q !== lastWrittenQ.current) {
      lastWrittenQ.current = params.q;
      setQInput(params.q);
    }
  }, [params.q]);
  useEffect(() => {
    if (qInput === lastWrittenQ.current) return;
    const id = setTimeout(() => {
      lastWrittenQ.current = qInput;
      writeParams({ q: qInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [qInput, writeParams]);

  const clearAll = () => {
    lastWrittenQ.current = "";
    setQInput("");
    setSearchParams(toVaultParams(DEFAULT_VAULT_PARAMS), { replace: true });
  };

  const pageClass = "mx-auto w-full max-w-6xl px-4 py-6 xl:px-8";

  if (items.isPending) {
    return (
      <Page titleKey="nav.vault" focusKey={focusKey} className={pageClass}>
        <p aria-busy="true" className="text-muted-foreground">
          {t("common.loading")}
        </p>
      </Page>
    );
  }
  if (!items.data) {
    return (
      <Page titleKey="nav.vault" focusKey={focusKey} className={pageClass}>
        <div data-testid="vault-error">
          <Recovery action={toRecoveryAction(items.error)} onRetry={() => void items.refetch()} retryable />
        </div>
      </Page>
    );
  }

  const all = items.data;
  const active = all.filter((i) => !i.archived);
  const hiddenCount = all.length - active.length;
  const uncovered = dashboard.data ? uncoveredSet(dashboard.data.vault) : null;
  const nameOf = (i: VaultItem) => {
    const n = itemName(i);
    return "text" in n ? n.text : t(n.labelKey);
  };

  if (!params.hidden && active.length === 0) {
    return (
      <Page titleKey="nav.vault" focusKey={focusKey} className={pageClass}>
        <div data-testid="vault" className="flex flex-col gap-4">
          <EmptyState
            data-testid="vault-first-time"
            headingRef={firstTimeHeadingRef}
            title={t("vault.firstTimeEmpty.title")}
            action={
              <Button asChild variant="cta" size="touch">
                <Link to="/vault/add">{t("vault.firstTimeEmpty.action")}</Link>
              </Button>
            }
          />
          {hiddenCount > 0 ? (
            <p>
              <Link
                to="/vault?hidden=1"
                className="inline-flex min-h-11 items-center font-semibold text-primary underline underline-offset-4"
              >
                {t("vault.showHiddenLink", { count: hiddenCount })}
              </Link>
            </p>
          ) : null}
        </div>
      </Page>
    );
  }

  const tiles = resolveProductTiles(config.productTiles, flags.data);
  // Only the current view's items: the normal view never offers an archived-only category.
  const options = categoryOptions(
    tiles,
    all.filter((i) => !!i.archived === params.hidden),
  );
  if (params.category !== "all" && !options.some((o) => o.value === params.category)) {
    options.push({ value: params.category, labelKey: categoryLabelKey(params.category) });
  }
  const visible = filterItems(all, params, { uncovered, displayName: nameOf });
  const vaultData = dashboard.data?.vault;
  const bannerTo = params.hidden ? undefined : expiredBannerRoute(vaultData);

  return (
    <Page titleKey="nav.vault" focusKey={focusKey} className={pageClass}>
      <div data-testid="vault" className="flex flex-col gap-6">
        {!params.hidden && vaultData ? (
          <VaultSummary
            data-testid="vault-summary"
            title={t("vault.summary.title")}
            stats={[
              { id: "insured", label: t("vault.summary.insured"), value: format.number(vaultData.insuredCount) },
              {
                id: "notCovered",
                label: t("vault.summary.notCovered"),
                value: format.number(vaultData.uninsuredCount),
                href: "/vault?status=uninsured",
              },
              { id: "insuredValue", label: t("vault.summary.insuredValue"), value: format.currency(vaultData.insuredValue) },
              { id: "total", label: t("vault.summary.total"), value: format.currency(vaultTotal(all, config.currency)) },
            ]}
            renderLink={({ href, className, "aria-label": ariaLabel, children }) => (
              <Link to={href} className={className} aria-label={ariaLabel}>
                {children}
              </Link>
            )}
          />
        ) : null}

        {bannerTo ? (
          <InfoBanner
            data-testid="vault-expired-banner"
            tone="warning"
            title={t("home.expiredTitle")}
            action={
              <Button asChild variant="outline" size="touch">
                <Link to={bannerTo}>
                  {vaultData && vaultData.lapsedItems.length === 1
                    ? t("vault.expiredBanner.choosePlan")
                    : t("vault.expiredBanner.viewNotCovered")}
                </Link>
              </Button>
            }
          />
        ) : null}

        <FilterBar
          qInput={qInput}
          onQInput={setQInput}
          params={params}
          options={options}
          onChange={writeParams}
          onClear={clearAll}
        />

        <VaultList
          visible={visible}
          params={params}
          uncovered={uncovered}
          nameOf={nameOf}
          credit={credit.data}
          format={format}
          onClear={clearAll}
          onBackToVault={() => writeParams({ hidden: false })}
          onChanged={async () => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: vaultQueryKeys.items() }),
              queryClient.invalidateQueries({ queryKey: homeQueryKeys.dashboard() }),
            ]);
          }}
          setArchivedInCache={(id, archived) => {
            const next = queryClient.setQueryData<VaultItem[]>(vaultQueryKeys.items(), (prev) =>
              prev?.map((i) => (i._id === id ? { ...i, archived } : i)),
            );
            if (archived && next?.every((i) => i.archived)) focusFirstTime.current = true;
          }}
        />
      </div>
    </Page>
  );
}

function FilterBar({
  qInput,
  onQInput,
  params,
  options,
  onChange,
  onClear,
}: {
  qInput: string;
  onQInput: (q: string) => void;
  params: VaultParams;
  options: Array<{ value: string; labelKey: string }>;
  onChange: (patch: Partial<VaultParams>) => void;
  onClear: () => void;
}) {
  const t = useT();
  const showClear = hasActiveFilters({ ...params, q: qInput }) || params.hidden;
  return (
    <div data-testid="vault-filters" className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
      <div className="flex min-w-0 flex-1 flex-col gap-1.5 md:min-w-64">
        <Label htmlFor="vault-search">{t("vault.search.label")}</Label>
        <Input
          id="vault-search"
          type="search"
          value={qInput}
          placeholder={t("vault.search.placeholder")}
          onChange={(e) => onQInput(e.target.value)}
          className="min-h-11"
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label id="vault-status-label" htmlFor="vault-status">
          {t("vault.filters.status")}
        </Label>
        <Select value={params.status} onValueChange={(v) => onChange({ status: v as VaultParams["status"] })}>
          <SelectTrigger id="vault-status" aria-labelledby="vault-status-label vault-status" className="min-h-11 w-full md:w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label id="vault-category-label" htmlFor="vault-category">
          {t("vault.filters.category")}
        </Label>
        <Select value={params.category} onValueChange={(v) => onChange({ category: v })}>
          <SelectTrigger
            id="vault-category"
            aria-labelledby="vault-category-label vault-category"
            className="min-h-11 w-full md:w-44"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vault.filters.all")}</SelectItem>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {t(o.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex min-h-11 items-center gap-2">
        <Switch
          id="vault-hidden"
          checked={params.hidden}
          onCheckedChange={(checked) => onChange({ hidden: checked })}
        />
        <Label htmlFor="vault-hidden">{t("vault.filters.showHidden")}</Label>
      </div>
      {showClear ? (
        <Button type="button" variant="outline" size="touch" onClick={onClear} data-testid="vault-clear">
          {t("vault.filters.clear")}
        </Button>
      ) : null}
    </div>
  );
}

function VaultList({
  visible,
  params,
  uncovered,
  nameOf,
  credit,
  format,
  onClear,
  onBackToVault,
  onChanged,
  setArchivedInCache,
}: {
  visible: VaultItem[];
  params: VaultParams;
  uncovered: ReadonlySet<string> | null;
  nameOf: (i: VaultItem) => string;
  credit: { amount: number; currency: string; usable: boolean } | undefined;
  format: Formatter;
  onClear: () => void;
  onBackToVault: () => void;
  onChanged: () => Promise<void>;
  setArchivedInCache: (id: string, archived: boolean) => void;
}) {
  const t = useT();
  const { vaultApi, config } = useServices();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [failed, setFailed] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [focusTarget, setFocusTarget] = useState<FocusTarget>(null);
  const actionChosen = useRef(false);

  // Per-currency formatters for items valued in another currency.
  const formatters = useRef(new Map<string, Formatter>());
  const formatValue = (item: VaultItem): string | undefined => {
    const v = displayValue(item);
    if (v == null) return undefined;
    const cur = item.valueCurrency;
    if (!cur || cur === config.currency) return format.currency(v);
    let f = formatters.current.get(cur);
    if (!f) {
      f = createFormatter({ locale: config.locale, currency: cur, timeZone: config.timeZone });
      formatters.current.set(cur, f);
    }
    return f.currency(v);
  };

  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget.kind === "item") {
      const el = document.querySelector<HTMLElement>(`[data-item-link="${CSS.escape(focusTarget.id)}"]`);
      if (el) el.focus();
      else headingRef.current?.focus();
    } else {
      headingRef.current?.focus();
    }
    setFocusTarget(null);
  }, [focusTarget, visible]);

  const toggle = async (item: VaultItem, index: number) => {
    const name = nameOf(item);
    const archive = !item.archived;
    setFailed((f) => {
      const { [item._id]: _gone, ...rest } = f;
      return rest;
    });
    setBusy((b) => ({ ...b, [item._id]: true }));
    announce(t(archive ? "vault.announce.hiding" : "vault.announce.unhiding", { name }));
    try {
      await vaultApi.setArchived(item._id, archive);
    } catch (err) {
      setBusy((b) => ({ ...b, [item._id]: false }));
      setFailed((f) => ({ ...f, [item._id]: err }));
      return;
    }
    const next = visible[index + 1] ?? visible[index - 1];
    setArchivedInCache(item._id, archive);
    setBusy((b) => ({ ...b, [item._id]: false }));
    announce(t(archive ? "vault.announce.hidden" : "vault.announce.unhidden", { name }));
    setFocusTarget(next ? { kind: "item", id: next._id } : { kind: "heading" });
    void onChanged();
  };

  let body: React.ReactNode;
  if (visible.length === 0) {
    const filtered = hasActiveFilters(params);
    body =
      params.hidden && !filtered ? (
        <EmptyState
          data-testid="vault-hidden-empty"
          headingLevel={3}
          title={t("vault.hiddenEmpty.title")}
          action={
            <Button type="button" variant="outline" size="touch" onClick={onBackToVault}>
              {t("vault.hiddenEmpty.action")}
            </Button>
          }
        />
      ) : (
        <EmptyState
          data-testid="vault-filtered-empty"
          headingLevel={3}
          title={t("vault.filteredEmpty.title")}
          action={
            <Button type="button" variant="outline" size="touch" onClick={onClear}>
              {t("vault.filters.clear")}
            </Button>
          }
        />
      );
  } else {
    body = (
      <ul data-testid="vault-items" className="grid grid-cols-1 gap-3 xl:grid-cols-3">
        {visible.map((item, index) => {
          const name = nameOf(item);
          const display = toDisplayStatus(item.status);
          const notCovered = isNotCovered(item, uncovered);
          const risk = params.hidden ? undefined : valueAtRisk(item, notCovered, config.currency);
          const chip = params.hidden
            ? undefined
            : creditChipFor(item, { notCovered, tiles: config.productTiles, credit, currency: config.currency });
          const error = failed[item._id];
          return (
            <li key={item._id} data-item-id={item._id} className="flex flex-col gap-2">
              <ItemCard
                aria-busy={busy[item._id] ? true : undefined}
                name={name}
                link={<Link to={itemRoute(item._id)} data-item-link={item._id} />}
                photoUrl={itemPhoto(item)}
                photoLabel={t("vault.card.photo", { name })}
                fallbackIcon={categoryIcon(item.classification)}
                value={formatValue(item)}
                menuLabel={t("vault.card.moreActions", { name })}
                menuItems={[
                  {
                    id: item.archived ? "unhide" : "hide",
                    label: t(item.archived ? "vault.card.unhide" : "vault.card.hide"),
                    onSelect: () => {
                      actionChosen.current = true;
                      void toggle(item, index);
                    },
                  },
                ]}
                onMenuCloseAutoFocus={(e) => {
                  // We move focus ourselves after the server answers.
                  if (actionChosen.current) {
                    e.preventDefault();
                    actionChosen.current = false;
                  }
                }}
              >
                <StatusBadge status={display.status} label={t(`status.${display.status}`)} />
                {display.claimNote ? <p className="text-sm text-muted-foreground">{t("vault.claimNote")}</p> : null}
                {risk != null ? <ValueAtRisk label={t("vault.valueAtRisk", { value: format.currency(risk) })} /> : null}
                {chip != null ? <CreditChip label={t("vault.credit", { amount: format.currency(chip) })} /> : null}
              </ItemCard>
              {error ? <InlineError error={error} onRetry={() => void toggle(item, index)} /> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <section aria-labelledby="vault-list-heading" className="flex flex-col gap-3">
      <h2 id="vault-list-heading" ref={headingRef} tabIndex={-1} className="text-lg font-semibold">
        {t("vault.listHeading")}
      </h2>
      {body}
    </section>
  );
}

/** A failed hide/unhide: a sibling after the card that takes focus; its aria-live is the only announcement. */
function InlineError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [error]);
  return (
    <div ref={ref} tabIndex={-1} data-testid="vault-item-error">
      <Recovery action={toRecoveryAction(error)} onRetry={onRetry} retryable />
    </div>
  );
}
